import React, { useState, useEffect, useRef } from 'react'
import {
  Calendar as CalendarIcon,
  ChevronLeft,
  ChevronRight,
  Clock,
  Check,
  X
} from 'lucide-react'
import { motion, AnimatePresence } from 'framer-motion'
import { format,
  addMonths,
  subMonths,
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  addDays,
  isSameMonth,
  isSameDay,
  isToday,
  parseISO,
  formatISO
} from 'date-fns'

const ModernCalendar = ({
  value,
  onChange,
  placeholder = "Select date and time",
  className = "",
  disabled = false,
  includeTime = true,
  minDate = null,
  maxDate = null,
  useAbsolutePositioning = false
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const [currentMonth, setCurrentMonth] = useState(new Date())
  const [selectedDate, setSelectedDate] = useState(value ? parseISO(value) : null)
  const [selectedTime, setSelectedTime] = useState(
    value ? format(parseISO(value), 'HH:mm') : format(new Date(), 'HH:mm')
  )
  const [timeInputs, setTimeInputs] = useState({
    hours: value ? format(parseISO(value), 'HH') : format(new Date(), 'HH'),
    minutes: value ? format(parseISO(value), 'mm') : format(new Date(), 'mm')
  })

  const containerRef = useRef(null)
  const calendarRef = useRef(null)
  const [calendarPosition, setCalendarPosition] = useState({ top: 0, left: 0 })
  const [isInModal, setIsInModal] = useState(false)

  // Check if we're inside a modal that needs absolute positioning
  const isInsideModal = () => {
    return useAbsolutePositioning
  }

  // Update calendar position when opened
  useEffect(() => {
    if (isOpen && containerRef.current) {
      const updatePosition = () => {
        const inModal = isInsideModal()
        setIsInModal(inModal)

        if (inModal) {
          // Use simpler positioning for modals - just position relative to container
          setCalendarPosition({ top: 0, left: 0 })
        } else {
          // Use viewport positioning for regular pages
          const rect = containerRef.current.getBoundingClientRect()
          const viewportHeight = window.innerHeight
          const calendarHeight = 400 // Approximate height of calendar

          // Check if calendar would go below viewport
          const spaceBelow = viewportHeight - rect.bottom
          const shouldOpenUpward = spaceBelow < calendarHeight && rect.top > calendarHeight

          setCalendarPosition({
            top: shouldOpenUpward ? rect.top - calendarHeight - 4 : rect.bottom + 4,
            left: Math.max(16, Math.min(rect.left, window.innerWidth - 320 - 16)) // 320 is calendar width
          })
        }
      }

      // Use setTimeout to ensure DOM is fully updated
      setTimeout(updatePosition, 0)

      // Update position on scroll/resize
      const handleUpdate = () => updatePosition()
      window.addEventListener('scroll', handleUpdate, true)
      window.addEventListener('resize', handleUpdate)

      return () => {
        window.removeEventListener('scroll', handleUpdate, true)
        window.removeEventListener('resize', handleUpdate)
      }
    }
  }, [isOpen])

  // Close calendar when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Update internal state when value changes
  useEffect(() => {
    if (value) {
      const date = parseISO(value)
      setSelectedDate(date)
      setSelectedTime(format(date, 'HH:mm'))
      setTimeInputs({
        hours: format(date, 'HH'),
        minutes: format(date, 'mm')
      })
      setCurrentMonth(date)
    }
  }, [value])

  const handleDateSelect = (date) => {
    setSelectedDate(date)
    setCurrentMonth(date)

    if (!includeTime) {
      // If no time needed, still require manual confirmation
      // Don't emit immediately to prevent auto-approval
      // The user must click confirm button
    }
  }

  const handleTimeChange = (field, value) => {
    const newInputs = { ...timeInputs, [field]: value }
    setTimeInputs(newInputs)

    // Validate and update time
    const hours = parseInt(newInputs.hours) || 0
    const minutes = parseInt(newInputs.minutes) || 0

    if (hours >= 0 && hours <= 23 && minutes >= 0 && minutes <= 59) {
      const timeStr = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`
      setSelectedTime(timeStr)
    }
  }

  const handleConfirm = () => {
    if (selectedDate) {
      if (includeTime) {
        const [hours, minutes] = selectedTime.split(':')
        const finalDate = new Date(selectedDate)
        finalDate.setHours(parseInt(hours), parseInt(minutes), 0, 0)

        const isoString = formatISO(finalDate)
        onChange(isoString)
      } else {
        // For date-only, use the selected date at midnight
        const finalDate = new Date(selectedDate)
        finalDate.setHours(0, 0, 0, 0)

        const isoString = formatISO(finalDate)
        onChange(isoString)
      }
      setIsOpen(false)
    }
  }

  const handleClear = () => {
    setSelectedDate(null)
    setSelectedTime(format(new Date(), 'HH:mm'))
    setTimeInputs({
      hours: format(new Date(), 'HH'),
      minutes: format(new Date(), 'mm')
    })
    onChange('')
    setIsOpen(false)
  }

  const renderCalendarGrid = () => {
    const monthStart = startOfMonth(currentMonth)
    const monthEnd = endOfMonth(currentMonth)
    const startDate = startOfWeek(monthStart)
    const endDate = endOfWeek(monthEnd)

    const days = []
    let day = startDate

    while (day <= endDate) {
      days.push(day)
      day = addDays(day, 1)
    }

    const weekDays = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa']

    return (
      <div style={{ padding: '1rem' }}>
        {/* Header */}
        <div className="flex items-center justify-between" style={{ marginBottom: '1rem' }}>
          <motion.button
            type="button"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={(e) => {
              e.preventDefault()
              e.stopPropagation()
              setCurrentMonth(subMonths(currentMonth, 1))
            }}
            className="btn-ghost btn-icon"
            style={{ color: 'var(--text-soft)' }}
          >
            <ChevronLeft style={{ width: '1rem', height: '1rem' }} />
          </motion.button>

          <h3 className="font-semibold" style={{ fontSize: '1rem', color: 'var(--text)' }}>
            {format(currentMonth, 'MMMM yyyy')}
          </h3>

          <motion.button
            type="button"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={(e) => {
              e.preventDefault()
              e.stopPropagation()
              setCurrentMonth(addMonths(currentMonth, 1))
            }}
            className="btn-ghost btn-icon"
            style={{ color: 'var(--text-soft)' }}
          >
            <ChevronRight style={{ width: '1rem', height: '1rem' }} />
          </motion.button>
        </div>

        {/* Week days */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '0.25rem', marginBottom: '0.5rem' }}>
          {weekDays.map((day) => (
            <div
              key={day}
              className="flex items-center justify-center font-medium"
              style={{ height: '2rem', fontSize: '0.75rem', color: 'var(--text-muted)' }}
            >
              {day}
            </div>
          ))}
        </div>

        {/* Calendar grid */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '0.25rem' }}>
          {days.map((day, index) => {
            const isCurrentMonth = isSameMonth(day, currentMonth)
            const isSelected = selectedDate && isSameDay(day, selectedDate)
            const isTodayDate = isToday(day)
            const isPastDate = minDate && day < minDate
            const isFutureDate = maxDate && day > maxDate
            const isDisabled = isPastDate || isFutureDate

            let dayBg = 'transparent'
            let dayColor = isCurrentMonth ? 'var(--text)' : 'var(--text-faint)'
            let dayBorder = 'none'
            let dayCursor = isDisabled ? 'not-allowed' : 'pointer'
            let dayOpacity = isDisabled ? 0.4 : 1

            if (isSelected) {
              dayBg = 'linear-gradient(to right, var(--accent), var(--accent-2))'
              dayColor = '#fff'
            } else if (isTodayDate && !isDisabled) {
              dayBg = 'var(--accent-soft)'
              dayColor = 'var(--accent)'
            }

            return (
              <motion.button
                type="button"
                key={day.toString()}
                whileHover={!isDisabled ? { scale: 1.05 } : {}}
                whileTap={!isDisabled ? { scale: 0.95 } : {}}
                onClick={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  if (!isDisabled) handleDateSelect(day)
                }}
                disabled={isDisabled}
                style={{
                  width: '2rem',
                  height: '2rem',
                  borderRadius: 'var(--radius-sm)',
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  position: 'relative',
                  overflow: 'hidden',
                  background: dayBg,
                  color: dayColor,
                  border: dayBorder,
                  cursor: dayCursor,
                  opacity: dayOpacity,
                  transition: 'all 0.15s ease',
                  boxShadow: isSelected ? 'var(--shadow-sm)' : 'none'
                }}
              >
                <span style={{ position: 'relative', zIndex: 1 }}>
                  {format(day, 'd')}
                </span>

                {/* Today indicator */}
                {isTodayDate && !isSelected && (
                  <div style={{
                    position: 'absolute',
                    bottom: '0.25rem',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    width: '0.25rem',
                    height: '0.25rem',
                    background: 'var(--accent)',
                    borderRadius: '50%'
                  }} />
                )}
              </motion.button>
            )
          })}
        </div>
      </div>
    )
  }

  const renderTimePicker = () => {
    if (!includeTime) return null

    return (
      <div style={{ borderTop: '1px solid var(--border)', padding: '1rem' }}>
        <div className="flex items-center gap-1" style={{ marginBottom: '0.75rem', gap: '0.75rem' }}>
          <Clock style={{ width: '1rem', height: '1rem', color: 'var(--text-muted)' }} />
          <span className="font-medium" style={{ fontSize: '0.875rem', color: 'var(--text-soft)' }}>
            Time
          </span>
        </div>

        <div className="flex items-center" style={{ gap: '0.5rem' }}>
          <div className="flex items-center" style={{ gap: '0.25rem' }}>
            <input
              type="number"
              min="0"
              max="23"
              value={timeInputs.hours}
              onChange={(e) => handleTimeChange('hours', e.target.value)}
              className="input"
              style={{
                width: '3rem',
                height: '2rem',
                textAlign: 'center',
                fontSize: '0.875rem',
                appearance: 'textfield',
                MozAppearance: 'textfield'
              }}
            />
            <span style={{ color: 'var(--text-muted)' }}>:</span>
            <input
              type="number"
              min="0"
              max="59"
              value={timeInputs.minutes}
              onChange={(e) => handleTimeChange('minutes', e.target.value)}
              className="input"
              style={{
                width: '3rem',
                height: '2rem',
                textAlign: 'center',
                fontSize: '0.875rem',
                appearance: 'textfield',
                MozAppearance: 'textfield'
              }}
            />
          </div>

          <div style={{ flex: 1, textAlign: 'center' }}>
            <div className="font-medium" style={{
              fontSize: '1rem',
              fontFamily: 'var(--font-mono)',
              color: 'var(--text-soft)'
            }}>
              {selectedTime}
            </div>
          </div>
        </div>
      </div>
    )
  }

  const formatDisplayValue = () => {
    if (!selectedDate) return placeholder

    if (includeTime) {
      return format(selectedDate, 'MMM d, yyyy \'at\' h:mm a')
    }

    return format(selectedDate, 'MMM d, yyyy')
  }

  return (
    <div ref={containerRef} style={{ position: 'relative' }} className={className}>
      {/* Trigger Button */}
      <motion.button
        type="button"
        onClick={(e) => {
          e.preventDefault()
          e.stopPropagation()
          if (!disabled) setIsOpen(!isOpen)
        }}
        disabled={disabled}
        style={{
          width: '100%',
          padding: '0.5rem 0.75rem',
          textAlign: 'left',
          border: `1px solid ${isOpen ? 'var(--accent)' : 'var(--border)'}`,
          borderRadius: 'var(--radius)',
          background: 'var(--surface)',
          color: 'var(--text)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          cursor: disabled ? 'not-allowed' : 'pointer',
          opacity: disabled ? 0.5 : 1,
          outline: 'none',
          boxShadow: isOpen ? '0 0 0 2px var(--accent-soft)' : 'none',
          transition: 'all 0.2s ease'
        }}
      >
        <span style={{ color: selectedDate ? 'var(--text)' : 'var(--text-muted)' }}>
          {formatDisplayValue()}
        </span>

        <CalendarIcon style={{ width: '1rem', height: '1rem', color: 'var(--text-muted)', flexShrink: 0 }} />
      </motion.button>

      {/* Calendar Dropdown */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            ref={calendarRef}
            initial={{ opacity: 0, y: -10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -10, scale: 0.95 }}
            transition={{ duration: 0.2, ease: "easeOut" }}
            style={{
              position: isInModal ? 'absolute' : 'fixed',
              zIndex: 9999,
              width: '320px',
              background: 'var(--surface)',
              border: '1px solid var(--border)',
              borderRadius: 'var(--radius-lg)',
              boxShadow: 'var(--shadow-lg)',
              backdropFilter: 'blur(10px)',
              ...(isInModal ? {
                top: 'calc(100% + 4px)',
                left: 0,
              } : {
                top: calendarPosition.top,
                left: calendarPosition.left,
              })
            }}
          >
            {renderCalendarGrid()}
            {renderTimePicker()}

            {/* Action buttons */}
            <div className="flex justify-end" style={{
              borderTop: '1px solid var(--border)',
              padding: '1rem',
              gap: '0.5rem'
            }}>
              <motion.button
                type="button"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  handleClear()
                }}
                className="btn btn-ghost btn-icon btn-sm"
              >
                <X style={{ width: '1rem', height: '1rem' }} />
              </motion.button>

              <motion.button
                type="button"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  handleConfirm()
                }}
                disabled={!selectedDate}
                className="btn btn-primary btn-sm flex items-center"
                style={{
                  gap: '0.25rem',
                  opacity: !selectedDate ? 0.5 : 1,
                  cursor: !selectedDate ? 'not-allowed' : 'pointer'
                }}
              >
                <Check style={{ width: '1rem', height: '1rem' }} />
                Confirm
              </motion.button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

export default ModernCalendar
